import { forwardRef, useEffect, useImperativeHandle } from "react"
import { Trans, useTranslation } from "react-i18next"
import {
  TextInput,
  Button,
  Group,
  Select,
  NumberInput,
  Alert,
  Paper,
  Radio,
  Stack,
} from "@mantine/core"
import { DateInput } from "@mantine/dates"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import type { TravelersCheckDetail, TravelersCheckFormValues } from "../models/travelersCheck"
import { STATE_MAP } from "../models/travelersCheck"
import {
  useCreateTravelersCheck,
  useUpdateTravelersCheck,
  useCheckAgencies,
} from "../hooks/useTravelersChecks"

interface Props {
  check?: TravelersCheckDetail | null
  onSuccess: () => void
}

const TravelersCheckFormContent = forwardRef<{ submit: () => void }, Props>(({ check, onSuccess }, ref) => {
  const { t } = useTranslation()
  const isEdit = !!check

  const { data: agencies } = useCheckAgencies()
  const createMutation = useCreateTravelersCheck()
  const updateMutation = useUpdateTravelersCheck()

  const form = useForm<TravelersCheckFormValues>({
    initialValues: {
      issuing_agency_id: 0,
      client_name: "",
      issuing_file: null,
      redeem_file: null,
      value: null,
      value_in_words: "",
      valid_until: null,
      state: "created",
    },
    validate: {
      issuing_agency_id: (v) => (v ? null : t("travelersChecks.validation.issuingAgencyRequired")),
      ...(isEdit ? {
        client_name: (v: string) => (v?.length ? null : t("travelersChecks.validation.clientNameRequired")),
        issuing_file: (v: number | null) => (v ? null : t("travelersChecks.validation.issuingFileRequired")),
        value: (v: number | null) => (v ? null : t("travelersChecks.validation.valueRequired")),
        value_in_words: (v: string) => (v?.length ? null : t("travelersChecks.validation.valueInWordsRequired")),
        valid_until: (v: string | null) => (v ? null : t("travelersChecks.validation.validUntilRequired")),
      } : {}),
    },
  })

  useEffect(() => {
    if (check) {
      form.setValues({
        issuing_agency_id: check.issuing_agency_id,
        client_name: check.client_name ?? "",
        issuing_file: check.issuing_file,
        redeem_file: check.redeem_file,
        value: check.value,
        value_in_words: check.value_in_words ?? "",
        valid_until: check.valid_until,
        state: check.state,
      })
    }
  }, [check])

  const handleSubmit = (values: TravelersCheckFormValues) => {
    if (isEdit && check) {
      updateMutation.mutate(
        { id: check.id, values },
        {
          onSuccess: () => {
            notifications.show({ color: "green", title: t("common.success"), message: t("travelersChecks.updateSuccess") })
            onSuccess()
          },
          onError: (err) => {
            notifications.show({ color: "red", title: t("common.error"), message: err.message })
          },
        },
      )
    } else {
      createMutation.mutate(values, {
        onSuccess: () => {
          notifications.show({ color: "green", title: t("common.success"), message: t("travelersChecks.createSuccess") })
          onSuccess()
        },
        onError: (err) => {
          notifications.show({ color: "red", title: t("common.error"), message: err.message })
        },
      })
    }
  }

  const agencyOptions = (agencies ?? []).map((a: { id: number; name: string }) => ({
    value: String(a.id),
    label: a.name,
  }))

  const isPending = createMutation.isPending || updateMutation.isPending
  const handleFormSubmit = form.onSubmit(handleSubmit)
  useImperativeHandle(ref, () => ({ submit: () => handleFormSubmit() }))
  const dateValue = form.values.valid_until ? new Date(form.values.valid_until) : null

  return (
    <form onSubmit={handleFormSubmit}>
      {(createMutation.isError || updateMutation.isError) && (
        <Alert color="red" mb="md" withCloseButton onClose={() => { createMutation.reset(); updateMutation.reset() }}>
          {createMutation.error?.message || updateMutation.error?.message}
        </Alert>
      )}

      {!isEdit ? (
        <Paper withBorder p="md" radius="md">
          <Select
            label={t("travelersChecks.issuingAgency")}
            placeholder={t("travelersChecks.selectAgency")}
            data={agencyOptions}
            searchable
            value={form.values.issuing_agency_id ? String(form.values.issuing_agency_id) : null}
            onChange={(val) => form.setFieldValue("issuing_agency_id", val ? Number(val) : 0)}
            error={form.errors.issuing_agency_id}
          />
        </Paper>
      ) : (
        <Stack gap="md">
          <Paper withBorder p="md" radius="md">
            <TextInput
              label={t("travelersChecks.clientName")}
              placeholder={t("travelersChecks.clientName")}
              {...form.getInputProps("client_name")}
            />
            <NumberInput
              label={t("travelersChecks.issuingFile")}
              placeholder="000000000"
              hideControls
              mt="md"
              {...form.getInputProps("issuing_file")}
            />
            <Group grow mt="md">
              <NumberInput
                label={t("travelersChecks.value")}
                placeholder="0.00"
                suffix=" €"
                decimalScale={2}
                fixedDecimalScale
                {...form.getInputProps("value")}
              />
              <DateInput
                label={t("travelersChecks.validUntil")}
                placeholder=""
                valueFormat="YYYY-MM-DD"
                value={dateValue}
                onChange={(val) => form.setFieldValue("valid_until", val ? val.toISOString().split("T")[0] : null)}
                error={form.errors.valid_until}
                clearable
              />
            </Group>
            <TextInput
              label={t("travelersChecks.valueInWords")}
              placeholder={t("travelersChecks.valueInWords")}
              mt="md"
              {...form.getInputProps("value_in_words")}
            />
          </Paper>

          <Paper withBorder p="md" radius="md">
            <Radio.Group
              value={form.values.state}
              onChange={(val) => form.setFieldValue("state", val)}
            >
              <Group>
                {Object.entries(STATE_MAP).filter(([key]) => key !== "deleted").map(([key, val]) => (
                  <Radio key={key} value={key} label={t(`travelersChecks.${key}`)} color={val.color} />
                ))}
              </Group>
            </Radio.Group>
          </Paper>
        </Stack>
      )}

    </form>
  )
})

export default TravelersCheckFormContent
